import { Recipient } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';

/**
 * Deduplicates an array of recipients based on their destination field.
 *
 * This function removes duplicate recipients by using the destination field as a unique key.
 * Recipients with empty, null, or whitespace-only destinations are filtered out.
 * When duplicates are found, the last occurrence in the array is kept.
 *
 * @param recipients - Array of recipient objects to deduplicate
 * @returns A new array containing only unique recipients with valid destinations
 *
 * @example
 * ```typescript
 * const recipients = [
 *   { destination: "user1@example.com", name: "John" },
 *   { destination: "user2@example.com", name: "Jane" },
 *   { destination: "user1@example.com", name: "John Updated" }, // Duplicate
 *   { destination: "", name: "Invalid" }, // Will be filtered out
 *   { destination: "   ", name: "Whitespace" }, // Will be filtered out
 * ];
 *
 * const unique = dedupeRecipients(recipients);
 * // Returns: [
 * //   { destination: "user2@example.com", name: "Jane" },
 * //   { destination: "user1@example.com", name: "John Updated" }
 * // ]
 * ```
 */
export const dedupeRecipients = (recipients: Recipient[]): Recipient[] => {
  const recipientMap = new Map<string, Recipient>();

  recipients.forEach((recipient) => {
    const destination = recipient.destination?.trim();
    if (destination) {
      recipientMap.set(destination, recipient);
    }
  });

  return Array.from(recipientMap.values());
};
